/**
 * Schermata di accesso utente
 * Gestisce login tramite access_key (parola chiave)
 */
export class UserAccessScreen {
  constructor(userSession, onLoginSuccess) {
    this.userSession = userSession;
    this.onLoginSuccess = onLoginSuccess;
    this.element = document.getElementById('user-access-screen'); // Usa l'elemento esistente
    this.isVisible = false;
    
    // Se l'elemento esiste, inizializzalo
    if (this.element) {
      this.initialize();
    } else {
      console.error('[UserAccessScreen] Elemento #user-access-screen non trovato!');
    }
  }

  /**
   * Inizializza la schermata
   */
  initialize() {
    if (!this.element) return;
    
    // Renderizza l'HTML
    this.element.innerHTML = this.getHTML();
    
    // Aggiungi le classi CSS
    this.element.className = 'view user-access-view';
    
    // Collega gli eventi
    this.bindEvents();
  }

  /**
   * Restituisce l'HTML della schermata
   * @returns {string}
   */
  getHTML() {
    return `
      <div class="access-container">
        <aside class="access-panel" aria-hidden="false">
          <div class="access-panel-top">
            <span class="access-panel-brand">● FANTAPP</span>
            <span class="access-panel-sys">SYS/AUCTION · v3</span>
          </div>
          <pre class="access-boot" aria-hidden="true">&gt; init sala_aste --stagione 25/26
&gt; carico listone ........... ok
&gt; verifico lega ............ ok
&gt; sincronizzo allenatori ... ok
&gt; attendo credenziale______</pre>
          <div class="access-resources">
            <div class="access-resources-head">
              <span>Risorse utili</span>
              <span>↗ link esterni</span>
            </div>
            <div class="access-resources-list">
              <a class="access-resource" href="https://www.fantacalcio.it/regolamento" target="_blank" rel="noopener noreferrer">
                <span class="access-resource-i">01</span>
                <span class="access-resource-body">
                  <span class="access-resource-n">Regolamento Mantra</span>
                  <span class="access-resource-sub">Ruoli, modificatori, bonus / malus</span>
                </span>
                <span class="access-resource-ext" aria-hidden="true">↗</span>
              </a>
              <a class="access-resource" href="https://www.fantacalcio.it/quotazioni-fantacalcio" target="_blank" rel="noopener noreferrer">
                <span class="access-resource-i">02</span>
                <span class="access-resource-body">
                  <span class="access-resource-n">Listone &amp; quotazioni</span>
                  <span class="access-resource-sub">Scarica XLSX · aggiornato</span>
                </span>
                <span class="access-resource-ext" aria-hidden="true">↗</span>
              </a>
              <a class="access-resource" href="https://www.fantacalcio.it/probabili-formazioni-serie-a" target="_blank" rel="noopener noreferrer">
                <span class="access-resource-i">03</span>
                <span class="access-resource-body">
                  <span class="access-resource-n">Probabili formazioni</span>
                  <span class="access-resource-sub">Titolari e ballottaggi</span>
                </span>
                <span class="access-resource-ext" aria-hidden="true">↗</span>
              </a>
            </div>
          </div>
          <div class="access-panel-foot">
            <div class="access-season">Stagione 2025 / 2026</div>
            <div class="access-season-meta">
              <span>Listone Fantacalcio</span>
              <span>Codice personale</span>
            </div>
          </div>
        </aside>

        <div class="access-main">
          <h1 class="access-hero">
            <span class="access-hero-line">Sala aste</span>
            <span class="access-hero-line access-hero-line--acc">Fantacalcio</span>
          </h1>

          <div class="access-form-container">
            <form id="access-form" class="access-form">
              <div class="form-group">
                <label for="access-key-input" class="form-label">
                  ▌ La tua parola chiave
                </label>
                <div class="access-key-row">
                  <input
                    id="access-key-input"
                    type="text"
                    class="form-input"
                    data-testid="access-code-input"
                    placeholder="FANTA2026"
                    maxlength="50"
                    required
                    autocomplete="off"
                    autofocus
                    spellcheck="false"
                  />
                  <button
                    type="submit"
                    id="login-btn"
                    class="btn btn-primary access-enter-btn"
                    data-testid="access-code-submit"
                  >
                    <span class="btn-text">Entra</span>
                    <span class="btn-loading" hidden>
                      <span class="spinner"></span>
                      …
                    </span>
                  </button>
                </div>
                <div class="form-help">
                  [i] Inserisci il codice di accesso fornito dall’amministratore
                </div>
              </div>
            </form>
          </div>

          <p class="access-footer-note">
            [i] I tuoi dati sono privati e accessibili solo a te
          </p>
        </div>
      </div>
    `;
  }

  /**
   * Collega gli eventi DOM
   */
  bindEvents() {
    const form = this.element.querySelector('#access-form');
    const input = this.element.querySelector('#access-key-input');
    const button = this.element.querySelector('#login-btn');

    // Submit form
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      await this.handleLogin();
    });

    // Validazione input in tempo reale
    input.addEventListener('input', (e) => {
      this.validateInput(e.target);
    });

    // Validazione su blur
    input.addEventListener('blur', (e) => {
      this.validateInput(e.target);
    });

    // Validazione su focus
    input.addEventListener('focus', (e) => {
      this.clearInputError(e.target);
    });

    // Tasto Enter
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        form.dispatchEvent(new Event('submit'));
      }
    });
  }

  /**
   * Valida l'input dell'access key
   * @param {HTMLInputElement} input - Elemento input
   */
  validateInput(input) {
    const value = input.value.trim();
    const isValid = this.isValidAccessKey(value);
    
    this.clearInputError(input);
    
    if (value && !isValid) {
      this.showInputError(input, 'Formato non valido. Usa solo lettere, numeri, trattini e underscore.');
    }
  }

  /**
   * Verifica se l'access key è valida
   * @param {string} accessKey - Access key da validare
   * @returns {boolean}
   */
  isValidAccessKey(accessKey) {
    if (!accessKey || accessKey.length < 3 || accessKey.length > 50) {
      return false;
    }
    
    // Solo lettere, numeri, trattini e underscore
    const validPattern = /^[a-zA-Z0-9_\-]+$/;
    return validPattern.test(accessKey);
  }

  /**
   * Mostra errore per un input
   * @param {HTMLInputElement} input - Elemento input
   * @param {string} message - Messaggio di errore
   */
  showInputError(input, message) {
    input.classList.add('input-error');

    const host = input.closest('.form-group') || input.parentNode;
    const existingError = host.querySelector('.input-error-message');
    if (existingError) {
      existingError.remove();
    }

    const errorElement = document.createElement('div');
    errorElement.className = 'input-error-message';
    errorElement.setAttribute('data-testid', 'access-error');
    errorElement.textContent = message;
    host.appendChild(errorElement);
  }

  /**
   * Pulisce l'errore di un input
   * @param {HTMLInputElement} input - Elemento input
   */
  clearInputError(input) {
    input.classList.remove('input-error');

    const host = input.closest('.form-group') || input.parentNode;
    const errorElement = host.querySelector('.input-error-message');
    if (errorElement) {
      errorElement.remove();
    }
  }

  /**
   * Gestisce il tentativo di login
   */
  async handleLogin() {
    const input = this.element.querySelector('#access-key-input');
    const button = this.element.querySelector('#login-btn');
    const buttonText = button.querySelector('.btn-text');
    const buttonLoading = button.querySelector('.btn-loading');
    
    const accessKey = input.value.trim();
    
    // Validazione finale
    if (!this.isValidAccessKey(accessKey)) {
      this.showInputError(input, 'Inserisci una parola chiave valida (3-50 caratteri)');
      input.focus();
      return;
    }

    // Disabilita form e mostra loading
    this.setFormState(false, true);
    
    try {
      // Tentativo login
      const result = await this.userSession.login(accessKey);
      
      if (result.success) {
        // Login riuscito
        this.showSuccessMessage('Accesso effettuato con successo!');
        
        // Callback di successo
        setTimeout(() => {
          if (this.onLoginSuccess) {
            this.onLoginSuccess(result.user);
          }
        }, 1000);
        
      } else {
        // Login fallito
        this.showInputError(input, result.error || 'Errore durante l\'accesso');
        input.focus();
      }
      
    } catch (error) {
      console.error('[ACCESS] Login error:', error);
      this.showInputError(input, 'Errore di connessione. Riprova più tardi.');
      input.focus();
      
    } finally {
      // Riabilita form
      this.setFormState(true, false);
    }
  }

  /**
   * Imposta lo stato del form (abilitato/disabilitato, loading)
   * @param {boolean} enabled - Se il form è abilitato
   * @param {boolean} loading - Se è in stato di caricamento
   */
  setFormState(enabled, loading) {
    const input = this.element.querySelector('#access-key-input');
    const button = this.element.querySelector('#login-btn');
    const buttonText = button.querySelector('.btn-text');
    const buttonLoading = button.querySelector('.btn-loading');
    
    input.disabled = !enabled;
    button.disabled = !enabled;
    
    if (loading) {
      buttonText.hidden = true;
      buttonLoading.hidden = false;
    } else {
      buttonText.hidden = false;
      buttonLoading.hidden = true;
    }
  }

  /**
   * Mostra messaggio di successo
   * @param {string} message - Messaggio da mostrare
   */
  showSuccessMessage(message) {
    const existingMessages = this.element.querySelectorAll('.success-message');
    existingMessages.forEach(msg => msg.remove());

    const messageElement = document.createElement('div');
    messageElement.className = 'success-message';
    messageElement.textContent = message;

    const form = this.element.querySelector('.access-form-container');
    form.appendChild(messageElement);

    setTimeout(() => {
      if (messageElement.parentNode) {
        messageElement.remove();
      }
    }, 5000);
  }

  /**
   * Renderizza e mostra la schermata
   */
  render() {
    this.show();
  }

  /**
   * Mostra la schermata
   */
  show() {
    if (this.element) {
      // Assicurati che l'elemento sia inizializzato
      if (!this.element.querySelector('.access-container')) {
        this.initialize();
      }
      
      this.element.style.display = 'block';
      this.element.hidden = false;
      this.isVisible = true;
      
      // Pulisce SEMPRE l'input e focus
      const input = this.element.querySelector('#access-key-input');
      if (input) {
        input.value = ''; // Forza pulizia campo
        this.clearInputError(input);
        setTimeout(() => input.focus(), 100); // Focus ritardato per garantire visibilità
      }
      
      console.log('[UserAccessScreen] Schermata mostrata e input pulito');
    } else {
      console.error('[UserAccessScreen] Elemento non trovato per show()');
    }
  }

  /**
   * Nasconde la schermata
   */
  hide() {
    if (this.element) {
      this.element.style.display = 'none';
      this.isVisible = false;
    }
  }

  /**
   * Verifica se la schermata è visibile
   * @returns {boolean}
   */
  isShown() {
    return this.isVisible;
  }

  /**
   * Pulisce il form
   */
  reset() {
    if (this.element) {
      const input = this.element.querySelector('#access-key-input');
      const form = this.element.querySelector('#access-form');
      
      if (input) {
        input.value = '';
        this.clearInputError(input);
      }
      
      if (form) {
        form.reset();
      }
      
      // Rimuovi messaggi
      const messages = this.element.querySelectorAll('.success-message, .input-error-message');
      messages.forEach(msg => msg.remove());
    }
  }
}
